import React, { createContext, useContext, useEffect, useState } from 'react'
import { supabase, isLocalDev } from '../lib/supabase'
import { trackCompleteRegistration } from '../lib/facebookConversions'

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [session, setSession] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Récupérer la session actuelle
    const getSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession()
        
        if (error) {
          console.error('Erreur lors de la récupération de la session:', error)
        } else {
          setSession(session)
          setUser(session?.user ?? null)
        }
      } catch (error) {
        console.error('Erreur de session:', error)
      } finally {
        setLoading(false)
      }
    }

    getSession()

    // Écouter les changements d'authentification
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state changed:', event, session?.user?.email)
        
        setSession(session)
        setUser(session?.user ?? null)
        setLoading(false)

        // Tracker l'événement de connexion réussie
        if (event === 'SIGNED_IN' && session?.user?.email) {
          try {
            await trackCompleteRegistration(session.user.email)
          } catch (error) {
            console.error('Erreur lors du tracking Facebook:', error)
          }
        }
      }
    )

    return () => subscription?.unsubscribe()
  }, [])

  // Connexion avec Magic Link ou bypass local
  const signInWithMagicLink = async (email) => {
    try {
      // Mode développement local : bypass Supabase
      if (isLocalDev) {
        console.log('🔧 Mode développement local - Connexion directe pour:', email)
        
        // Créer un utilisateur factice pour le développement
        const mockUser = {
          id: `local-dev-${Date.now()}`,
          email: email,
          user_metadata: {
            email: email,
            email_verified: true,
            login_type: 'local_dev'
          },
          app_metadata: {},
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }

        const mockSession = {
          access_token: 'local-dev-token',
          token_type: 'bearer',
          expires_in: 3600,
          expires_at: Math.floor(Date.now() / 1000) + 3600,
          refresh_token: 'local-dev-refresh',
          user: mockUser
        }

        // Mettre à jour l'état local
        setUser(mockUser)
        setSession(mockSession)
        setLoading(false)

        // Tracker l'événement de connexion
        try {
          await trackCompleteRegistration(email)
        } catch (error) {
          console.error('Erreur lors du tracking Facebook:', error)
        }

        return {
          data: { user: mockUser, session: mockSession },
          error: null,
          message: `✅ Connexion locale réussie pour ${email}`
        }
      }

      // Mode production : utiliser Supabase normalement
      const { data, error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          shouldCreateUser: true, // Permet la création automatique si nécessaire
          emailRedirectTo: `${window.location.origin}`,
          data: {
            // Ajouter des métadonnées pour différencier du signup classique
            login_type: 'magic_link'
          }
        }
      })

      if (error) {
        console.error('Erreur lors de l\'envoi du Magic Link:', error)
        return { data: null, error: error.message }
      }

      return {
        data, 
        error: null,
        message: 'Un lien de connexion magique a été envoyé à votre adresse email. Vérifiez votre boîte de réception.'
      }
    } catch (error) {
      console.error('Erreur lors de la connexion:', error)
      return { data: null, error: error.message }
    }
  }

  // Déconnexion
  const signOut = async () => {
    try {
      // En mode local, juste nettoyer l'état
      if (isLocalDev) {
        console.log('🔧 Déconnexion locale')
        setUser(null)
        setSession(null)
        return { error: null }
      }

      // En production, utiliser Supabase
      const { error } = await supabase.auth.signOut()
      
      if (error) {
        console.error('Erreur lors de la déconnexion:', error)
        return { error: error.message }
      }

      return { error: null }
    } catch (error) {
      console.error('Erreur de déconnexion:', error)
      return { error: error.message }
    }
  }

  const value = {
    user,
    session,
    loading,
    signInWithMagicLink,
    signOut,
    isLocalDev
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
} 